import React, { useEffect, useState, useContext, useMemo, useRef, useLayoutEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../../axios";
import invoiceApi from "../../api/invoiceApi";
import posSessionApi from "../../api/posSessionApi";
import { SettingsContext } from "../../contexts/SettingsContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";

/* ------------------------------------------------------------------ */
/* Small presentational helpers (no business logic)                    */
/* ------------------------------------------------------------------ */

const Icon = {
  cart: (c = "w-5 h-5") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.3 2.3c-.6.6-.2 1.7.7 1.7H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
  ),
  search: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" /></svg>
  ),
  barcode: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" d="M4 6v12M7 6v12M11 6v12M14 6v12M17 6v12M20 6v12" /></svg>
  ),
  user: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
  ),
  trash: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.9 12.1A2 2 0 0116.1 21H7.9a2 2 0 01-2-1.9L5 7m5 4v6m4-6v6M15 7V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
  ),
  percent: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" d="M19 5L5 19" /><circle cx="7" cy="7" r="2.5" /><circle cx="17" cy="17" r="2.5" /></svg>
  ),
  pencil: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.2 5.2l3.6 3.6M4 20l4.5-1 10.3-10.3a2.5 2.5 0 00-3.5-3.5L5 15.5 4 20z" /></svg>
  ),
  broom: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M9 21l-6-6 3-3 6 6-3 3z" /></svg>
  ),
  receipt: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6M9 11h6M9 15h3M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2V3z" /></svg>
  ),
  undo: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" /></svg>
  ),
  power: (c = "w-4 h-4") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" d="M12 3v9M6.3 6.3a8 8 0 1011.4 0" /></svg>
  ),
  chevron: (c = "w-5 h-5") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
  ),
  box: (c = "w-12 h-12") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
  ),
  backspace: (c = "w-6 h-6") => (
    <svg className={c} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9l4 4m0-4l-4 4M3 12l6-7h11a1 1 0 011 1v12a1 1 0 01-1 1H9l-6-7z" /></svg>
  ),
};

/* Modal shell used by every dialog on the screen */
function Modal({ children, maxWidth = "max-w-md", dim = "bg-slate-900/50" }) {
  return (
    <div className={`fixed inset-0 ${dim} backdrop-blur-[2px] flex items-center justify-center z-50 p-4`}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} max-h-[92vh] overflow-hidden flex flex-col`}>
        {children}
      </div>
    </div>
  );
}

/* Numeric keypad shared by the price pad and the payment modal */
function Keypad({ onKey, onBackspace, size = "h-14 text-xl" }) {
  const keys = ["7", "8", "9", "4", "5", "6", "1", "2", "3", ".", "0"];
  return (
    <div className="grid grid-cols-3 gap-2" dir="ltr">
      {keys.map(d => (
        <button
          key={d}
          type="button"
          onClick={() => onKey(d)}
          className={`${size} font-semibold tabular-nums rounded-xl bg-white border border-slate-200 text-slate-800 hover:bg-slate-50 active:bg-slate-100 active:scale-[0.97] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400`}
        >
          {d}
        </button>
      ))}
      <button
        type="button"
        onClick={onBackspace}
        className={`${size} flex items-center justify-center rounded-xl bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200 active:scale-[0.97] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400`}
        aria-label="Backspace"
      >
        {Icon.backspace()}
      </button>
    </div>
  );
}

export default function Pos() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { typeId: routeTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const queryTypeId = searchParams.get("typeId") || searchParams.get("invoiceTypeId") || searchParams.get("type");
  const returnOf = searchParams.get("returnOf");
  const initialTypeId = Number(routeTypeId || queryTypeId) || 11;
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const branchId = parseInt(localStorage.getItem("branchId"), 10);
  const userId = parseInt(localStorage.getItem("userId"), 10);
  const { decimalPlaces } = useContext(SettingsContext);

  // Security Context
  const restrictedSafeId = localStorage.getItem("safeId") ? parseInt(localStorage.getItem("safeId"), 10) : null;
  const restrictedStockId = localStorage.getItem("stockId") ? parseInt(localStorage.getItem("stockId"), 10) : null;
  const allowedSafeIds = localStorage.getItem("allowedSafeIds") ? JSON.parse(localStorage.getItem("allowedSafeIds")) : [];
  const allowedStockIds = localStorage.getItem("allowedStockIds") ? JSON.parse(localStorage.getItem("allowedStockIds")) : [];

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posSettings, setPosSettings] = useState(null); // Store POS settings
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [methods, setMethods] = useState([]);
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [settings, setSettings] = useState({ isPriceIncludesVAT: false, defaultCustomerId: 0, defaultPaymentMethodId: 0, defaultSafeId: 0, defaultBankId: 0, defaultStockId: 0, autoApproveInvoices: false });
  const [creditMethodId, setCreditMethodId] = useState(null);
  const [selectedLineIndex, setSelectedLineIndex] = useState(null);

  const [barcode, setBarcode] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnItems, setReturnItems] = useState([]);

  // Session State
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [startSessionModalOpen, setStartSessionModalOpen] = useState(false);
  const [openingBalance, setOpeningBalance] = useState("");

  const [closeSessionModalOpen, setCloseSessionModalOpen] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [closingBalance, setClosingBalance] = useState("");
  const [closingNotes, setClosingNotes] = useState("");
  const [parkedSlots, setParkedSlots] = useState(Array(9).fill(null)); // 9 أزرار

  useEffect(() => {
    if (!companyId) return;
    setSessionLoading(true);
    posSessionApi.getCurrentSession()
      .then(res => {
        setSession(res.data);
        setSessionLoading(false);
      })
      .catch(err => {
        setSessionLoading(false);
        if (err.response && err.response.status === 404) {
          setStartSessionModalOpen(true);
        } else {
          // toast.error("Failed to check POS session");
        }
      });
  }, [companyId]);

  async function handleStartSession() {
    const balance = parseFloat(openingBalance);
    if (isNaN(balance)) { toast.error(t('pos.messages.amountInvalid')); return; }
    try {
      const res = await posSessionApi.startSession({ openingBalance: balance });
      setSession(res.data);
      setStartSessionModalOpen(false);
      toast.success(t('pos.session.sessionStarted'));
    } catch (err) {
      toast.error("Error starting session");
    }
  }

  async function handleCloseSessionClick() {
    try {
      const res = await posSessionApi.getSessionSummary();
      setSessionSummary(res.data);
      setClosingBalance(""); // Let user enter it
      setCloseSessionModalOpen(true);
    } catch (err) {
      toast.error("Error fetching session summary");
    }
  }

  async function handleConfirmCloseSession() {
    const actual = parseFloat(closingBalance);
    if (isNaN(actual)) { toast.error(t('pos.messages.amountInvalid')); return; }

    try {
      const res = await posSessionApi.closeSession({
        closingBalance: actual,
        notes: closingNotes
      });

      if (res.data.warning) {
        toast.warning(res.data.warning, { autoClose: 10000 });
      } else {
        toast.success(t('pos.session.sessionClosed'));
      }

      setCloseSessionModalOpen(false);
      setSession(null);
      setSessionSummary(null);

      // Open Report in new tab
      window.open(`/reports/pos-session/${res.data.id}`, '_blank');

      // Restart flow -> Show Start Modal
      setStartSessionModalOpen(true);
      setOpeningBalance("");
    } catch (err) {
      toast.error("Error closing session");
    }
  }

  const [form, setForm] = useState({
    invoiceTypeId: initialTypeId,
    invoiceNumber: "",
    issueDate: new Date().toLocaleDateString("en-CA"),
    referenceDate: new Date().toLocaleDateString("en-CA"),
    referenceNumber: "-1",
    invoiceStatus: 0,
    customerId: 0,
    supplierId: 0,
    customerPrevBalance: 0,
    stockId: 0,
    salesmanId: null,
    paymentMethodId: 0,
    safeId: 0,
    bankId: 0,
    paidAmount: 0,
    notes: "",
    discount: parseFloat(0).toFixed(decimalPlaces),
    lines: [],
  });

  const [padOpen, setPadOpen] = useState(false);
  const [padValue, setPadValue] = useState("");
  const [padTitle, setPadTitle] = useState("");
  const [padOnConfirm, setPadOnConfirm] = useState(null);
  const [padIsFirstKey, setPadIsFirstKey] = useState(true); // New state to track first key press
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payIsFirstKey, setPayIsFirstKey] = useState(true);
  const [payMethodId, setPayMethodId] = useState(0);
  const [paySafeId, setPaySafeId] = useState(0);
  const [payBankId, setPayBankId] = useState(0);
  const [payAmount, setPayAmount] = useState("");
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  const priceIncludesVAT = !!settings.isPriceIncludesVAT;

  useEffect(() => {
    if (!companyId) return;
    const load = (url, setter) =>
      axios.get(url)
        .then(r => setter(r.data || []))
        .catch(err => console.error(`Failed to load ${url}`, err));

    load(`/Product/by-company/${companyId}`, setProducts);
    load(`/Category/by-company/${companyId}`, setCategories);
    load(`/Customer/by-company/${companyId}`, setCustomers);
    load(`/PaymentMethod/by-company/${companyId}`, setMethods);
    load(`/Safe/by-company/${companyId}`, setSafes);
    load(`/Bank/by-company/${companyId}`, setBanks);
    load(`/Stock/by-company/${companyId}`, setStocks);

    // Fetch POS Settings
    axios.get('/PosSettings/by-branch', {
      params: {
        companyId,
        branchId,
        invoiceTypeId: initialTypeId // Use the current invoice type
      }
    })
      .then(res => setPosSettings(res.data))
      .catch(err => console.error("Failed to load POS Settings", err));

  }, [companyId, initialTypeId]);

  useEffect(() => {
    const credit = methods.find(m => m.nameAr === "آجل")?.id ?? null;
    setCreditMethodId(credit);
  }, [methods]);

  useEffect(() => {
    const cashLocal = methods.find(m => m.nameAr === "نقدي")?.id;
    if (payMethodId === cashLocal) {
      let safe = form.safeId || settings.defaultSafeId || 0;
      if (restrictedSafeId) safe = restrictedSafeId;
      else if (allowedSafeIds.length > 0 && !allowedSafeIds.includes(safe)) safe = allowedSafeIds[0];

      setPaySafeId(safe);
      setPayBankId(0);
    } else if (payMethodId !== cashLocal && payMethodId !== creditMethodId) {
      setPayBankId(form.bankId || settings.defaultBankId || 0);
      setPaySafeId(0);
    } else {
      setPaySafeId(0);
      setPayBankId(0);
    }
  }, [payMethodId, methods, form.safeId, form.bankId, settings.defaultSafeId, settings.defaultBankId, creditMethodId, restrictedSafeId, allowedSafeIds]);

  useEffect(() => {
    if (!companyId) return;
    axios.get("/ProgramSettings/by-company-and-type", { params: { companyId, invoiceTypeId: form.invoiceTypeId } })
      .then(r => {
        const s = r.data || {};
        setSettings(s);
        const cashId = methods.find(m => m.nameAr === "نقدي")?.id;

        // Determine Final Stock
        let finalStockId = s.defaultStockId || 0;
        if (restrictedStockId) {
          finalStockId = restrictedStockId;
        } else if (allowedStockIds.length > 0) {
          if (!allowedStockIds.includes(finalStockId)) {
            finalStockId = allowedStockIds[0];
          }
        }

        // Determine Final Safe
        let finalSafeId = s.defaultSafeId || 0;
        if (restrictedSafeId) {
          finalSafeId = restrictedSafeId;
        } else if (allowedSafeIds.length > 0) {
          if (!allowedSafeIds.includes(finalSafeId)) {
            finalSafeId = allowedSafeIds[0];
          }
        }

        setForm(f => ({
          ...f,
          customerId: s.defaultCustomerId || 0,
          paymentMethodId: s.defaultPaymentMethodId || 0,
          safeId: (s.defaultPaymentMethodId || 0) === cashId ? finalSafeId : 0,
          bankId: (s.defaultPaymentMethodId || 0) !== cashId ? (s.defaultBankId || 0) : 0,
          stockId: finalStockId,
        }));
        invoiceApi.getNextNumber(companyId, branchId, form.invoiceTypeId, userId).then(res => {
          // ✅ التوافق: إذا كانت الاستجابة object بها displayNumber نستخدمه، وإلا نستخدم القيمة القديمة
          const data = res.data;
          const displayNumber =
            (data && typeof data === "object" && data.displayNumber)
              ? data.displayNumber
              : (data?.toString?.() ?? data);
          setForm(f => ({ ...f, invoiceNumber: displayNumber }));
        });
      })
      .catch(() => { });
  }, [companyId, methods, branchId, form.invoiceTypeId, restrictedSafeId, restrictedStockId]);

  useEffect(() => {
    if (!returnOf) return;
    if (Number(form.invoiceTypeId) !== 3) return;
    if (returnModalOpen) return;
    if ((form.lines || []).length > 0) return;
    invoiceApi.getById(returnOf).then(async res => {
      const inv = res.data || {};
      const originalItems = inv.items || inv.invoiceItems || inv.details || [];
      let returnedMap = {};
      try {
        const allRes = await invoiceApi.getByCompany(companyId);
        const allInvoices = allRes.data || [];
        const relatedReturns = allInvoices.filter(x => Number(x.invoiceType) === 3 && String(x.referenceNumber || "") === String(inv.invoiceNumber || ""));
        relatedReturns.forEach(r => {
          (r.items || []).forEach(it => {
            const key = `${it.productId}-${it.unitId}`;
            const qty = parseFloat(it.quantity || 0);
            returnedMap[key] = (returnedMap[key] || 0) + qty;
          });
        });
      } catch { }
      const itemsForSelection = originalItems.map(it => {
        const pid = Number(it.productId);
        const p = products.find(pr => Number(pr.id) === pid);
        const unitsList = Array.isArray(p?.units) ? p.units : [];
        const u = unitsList.find(x => Number(x.unitId) === Number(it.unitId)) || unitsList.find(x => x.isBaseUnit) || {};
        const priceBase = parseFloat(it.unitPrice || 0);
        const taxPct = parseFloat(it.taxPercent || 0);
        const price = settings.isPriceIncludesVAT ? (priceBase * (1 + taxPct)) : priceBase;
        const key = `${it.productId}-${it.unitId}`;
        const returnedQty = parseFloat(returnedMap[key] || 0);
        const originalQty = parseFloat(it.quantity || 0);
        const availableQty = Math.max(0, originalQty - returnedQty);
        return {
          productId: pid,
          productName: p?.arabicName ?? p?.nameAr ?? it.description ?? "",
          description: it.description ?? "",
          unitId: u?.unitId || 0,
          unitName: u?.unitNameAr || "",
          originalQty,
          returnedQty,
          availableQty,
          selectQty: 0,
          price: parseFloat(price).toFixed(decimalPlaces),
          discount: parseFloat(it.discount || 0).toFixed(decimalPlaces),
          taxRate: parseFloat(taxPct).toFixed(decimalPlaces),
          unitsList,
        };
      });
      setReturnItems(itemsForSelection);
      setForm(f => ({
        ...f,
        invoiceTypeId: 3,
        referenceNumber: inv.invoiceNumber,
        referenceDate: (inv.issueDate || new Date().toISOString()).slice(0, 16),
        customerId: inv.customerId || f.customerId,
        stockId: inv.stockId || f.stockId,
      }));
      setReturnModalOpen(true);
    }).catch(() => { });
  }, [returnOf, products, decimalPlaces, settings.isPriceIncludesVAT, companyId, form.invoiceTypeId, form.lines, returnModalOpen]);

  function setReturnItemQty(i, v) {
    const val = Math.max(0, parseFloat(v || 0));
    setReturnItems(list => {
      const copy = [...list];
      const max = copy[i].availableQty;
      copy[i] = { ...copy[i], selectQty: Math.min(val, max) };
      return copy;
    });
  }

  function returnAll() {
    setReturnItems(list => list.map(it => ({ ...it, selectQty: it.availableQty })));
  }

  function applyReturnSelection() {
    const selected = returnItems.filter(it => it.selectQty > 0);
    if (selected.length === 0) { toast.error(t('pos.messages.addItemsFirst')); return; }
    const newLines = selected.map(it => ({
      productId: it.productId,
      productName: it.productName,
      description: it.description,
      unitId: it.unitId,
      unitName: it.unitName,
      qty: parseFloat(it.selectQty).toFixed(decimalPlaces),
      price: it.price,
      discount: it.discount,
      taxRate: it.taxRate,
      unitsList: it.unitsList,
    }));
    setForm(f => ({ ...f, lines: newLines }));
    setReturnModalOpen(false);
  }

  function exitReturnMode() {
    setForm(f => ({
      ...f,
      invoiceTypeId: 11,
      referenceNumber: "-1",
      referenceDate: new Date().toLocaleDateString("en-CA"),
      lines: [],
    }));
    setReturnItems([]);
    setReturnModalOpen(false);
    navigate('/pos?typeId=11', { replace: true });
  }

  useEffect(() => {
    if (Number(form.invoiceTypeId) !== 3 && returnModalOpen) {
      setReturnModalOpen(false);
    }
  }, [form.invoiceTypeId, returnModalOpen]);
  useEffect(() => {
    if (returnModalOpen && (form.lines || []).length > 0) {
      setReturnModalOpen(false);
    }
  }, [returnModalOpen, form.lines]);


  function pickUnitPriceForSale(u, p) {
    const sale = u?.saleprice ?? p?.saleprice ?? 0;
    return parseFloat(sale || 0).toFixed(decimalPlaces);
  }

  async function insertProductByBarcode(code) {
    const raw = (code || "").trim();
    if (!raw) return;
    try {
      setBarcodeLoading(true);
      let p = products.find(pr => (pr.barcode || "").toString() === raw);
      let chosenUnit = null;
      if (!p) {
        for (const pr of products) {
          const units = Array.isArray(pr.units) ? pr.units : [];
          const match = units.find(u => (u.barcode || "").toString() === raw);
          if (match) { p = pr; chosenUnit = match; break; }
        }
      }
      if (!p) { toast.error(t('pos.messages.barcodeNotFound')); return; }
      const unitsList = Array.isArray(p.units) ? p.units : [];
      const baseUnit = chosenUnit || unitsList.find(u => u.isBaseUnit) || {};

      setForm(f => {
        const idx = f.lines.findIndex(l => l.productId === p.id && l.unitId === (baseUnit.unitId || 0));
        let newLines = [...f.lines];
        let line;

        if (idx >= 0) {
          const existingLine = newLines[idx];
          const q = parseFloat(existingLine.qty) + 1;
          line = { ...existingLine, qty: q.toFixed(decimalPlaces) };
          newLines.splice(idx, 1);
        } else {
          line = {
            productId: p.id,
            productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
            description: p?.description ?? "",
            unitId: baseUnit?.unitId || 0,
            unitName: baseUnit?.unitNameAr || "",
            qty: parseFloat(1).toFixed(decimalPlaces),
            price: pickUnitPriceForSale(baseUnit, p),
            discount: parseFloat(0).toFixed(decimalPlaces),
            taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
            unitsList,
          };
        }
        newLines.push(line);
        return { ...f, lines: newLines };
      });
    } finally {
      setBarcode("");
      setBarcodeLoading(false);
    }
  }

  function handleBarcodeKeyDown(e) {
    if (e.key === "Enter") { e.preventDefault(); insertProductByBarcode(barcode); }
  }

  function handleQty(i, delta) {
    setForm(f => {
      const lines = [...f.lines];
      const q = Math.max(0, parseFloat(lines[i].qty) + delta);
      lines[i] = { ...lines[i], qty: q.toFixed(decimalPlaces) };
      return { ...f, lines };
    });
  }

  function handleRemoveLine(i) {
    setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));
  }

  function handleLineChange(i, field, raw) {
    const lines = [...form.lines];
    const ln = { ...lines[i] };
    if (field === "qty") ln.qty = raw;
    if (field === "price") ln.price = raw;
    if (field === "discount") ln.discount = raw;
    if (field === "taxRate") ln.taxRate = parseFloat(raw).toFixed(decimalPlaces);
    lines[i] = ln;
    setForm(f => ({ ...f, lines }));
  }

  function setQtyForSelected(q) {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setForm(f => {
      const lines = [...f.lines];
      lines[ti] = { ...lines[ti], qty: parseFloat(q).toFixed(decimalPlaces) };
      return { ...f, lines };
    });
    setSelectedLineIndex(ti);
  }

  function incSelected(delta) {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    handleQty(ti, delta);
    setSelectedLineIndex(ti);
  }

  function removeSelectedLine() {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.emptyCart')); return; }
    handleRemoveLine(ti);
    setSelectedLineIndex(null);
  }

  function clearCart() {
    setForm(f => ({ ...f, lines: [] }));
    setSelectedLineIndex(null);
  }

  function setLineDiscountPercent() {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setSelectedLineIndex(ti);
    setPadTitle(t('pos.pad.titleDiscountPercent'));
    setPadValue("");
    setPadIsFirstKey(true);
    setPadOnConfirm(() => (v) => {
      const pv = parseFloat(v);
      if (isNaN(pv)) { toast.error(t('pos.messages.percentInvalid')); return; }
      setForm(f => {
        const lines = [...f.lines];
        const ln = lines[ti];
        const qty = parseFloat(ln.qty) || 0;
        const price = parseFloat(ln.price) || 0;
        const disc = qty * price * (pv || 0) / 100;
        lines[ti] = { ...ln, discount: parseFloat(disc).toFixed(decimalPlaces) };
        return { ...f, lines };
      });
    });
    setPadOpen(true);
  }

  function setLineDiscountAmount() {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setSelectedLineIndex(ti);
    setPadTitle(t('pos.pad.titleDiscountAmount'));
    setPadValue("");
    setPadIsFirstKey(true);
    setPadOnConfirm(() => (v) => {
      const val = parseFloat(v);
      if (isNaN(val)) { toast.error(t('pos.messages.amountInvalid')); return; }
      setForm(f => {
        const lines = [...f.lines];
        lines[ti] = { ...lines[ti], discount: val.toFixed(decimalPlaces) };
        return { ...f, lines };
      });
    });
    setPadOpen(true);
  }

  function changeLinePrice() {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setSelectedLineIndex(ti);
    setPadTitle(t('pos.pad.titleEnterPrice'));
    setPadValue(String(form.lines[ti]?.price || ""));
    setPadIsFirstKey(true);
    setPadOnConfirm(() => (v) => {
      const val = parseFloat(v);
      if (isNaN(val)) { toast.error(t('pos.messages.priceInvalid')); return; }
      setForm(f => {
        const lines = [...f.lines];
        lines[ti] = { ...lines[ti], price: val.toFixed(decimalPlaces) };
        return { ...f, lines };
      });
    });
    setPadOpen(true);
  }

  function numPadBackspace() {
    setPadValue(v => v.slice(0, -1));
    setPadIsFirstKey(false);
  }

  function numPadClear() {
    setPadValue("");
    setPadIsFirstKey(true);
  }

  function handlePadKeyPress(digit) {
    if (padIsFirstKey) {
      setPadValue(digit);
      setPadIsFirstKey(false);
    } else {
      setPadValue(v => v + digit);
    }
  }

  function numPadConfirm() {
    if (padOnConfirm) padOnConfirm(padValue);
    setPadOpen(false);
  }

  function numPadCancel() {
    setPadOpen(false);
  }

  function openPaidAmountPad() {
    setPadTitle(t('pos.pad.titlePaidAmount'));
    setPadValue(String(form.paidAmount || 0));
    setPadIsFirstKey(true);
    setPadOnConfirm(() => (v) => {
      const val = parseFloat(v);
      if (isNaN(val)) { toast.error(t('pos.messages.paidAmountInvalid')); return; }
      setForm(f => ({ ...f, paidAmount: val }));
    });
    setPadOpen(true);
  }

  function openPaymentModal() {
    if ((form.lines || []).length === 0) { toast.error(t('pos.messages.emptyCart')); return; }

    const cashLocal = methods.find(m => m.nameAr === "نقدي")?.id;
    const defaultMethod = form.paymentMethodId || settings.defaultPaymentMethodId || 0;

    let initialMethod = defaultMethod;
    if (initialMethod === 0 && methods.length > 0) initialMethod = methods[0].id;

    setPayMethodId(initialMethod);

    // Determine safe/bank based on method
    if (initialMethod === cashLocal) {
      let safe = form.safeId || settings.defaultSafeId || 0;
      if (restrictedSafeId) safe = restrictedSafeId;
      else if (allowedSafeIds.length > 0 && !allowedSafeIds.includes(safe)) safe = allowedSafeIds[0];
      setPaySafeId(safe);
      setPayBankId(0);
    } else if (initialMethod !== creditMethodId) {
      setPayBankId(form.bankId || settings.defaultBankId || 0);
      setPaySafeId(0);
    } else {
      setPaySafeId(0);
      setPayBankId(0);
    }

    setPayAmount(totals.net.toFixed(decimalPlaces));
    setPayIsFirstKey(true);
    setPayModalOpen(true);
  }

  function openCustomerModal() {
    setCustomerSearch("");
    setCustomerModalOpen(true);
  }

  function chooseCustomer(id) {
    setForm(f => ({ ...f, customerId: Number(id) }));
    setCustomerModalOpen(false);
  }

  function cancelCustomer() {
    setCustomerModalOpen(false);
  }

  function togglePriceIncludesVAT() {
    setSettings(s => ({ ...s, isPriceIncludesVAT: !s.isPriceIncludesVAT }));
  }

  function handlePayKeyPress(digit) {
    if (payIsFirstKey) {
      setPayAmount(digit);
      setPayIsFirstKey(false);
    } else {
      setPayAmount(v => v + digit);
    }
  }

  function confirmPayment() {
    const amt = parseFloat(payAmount);
    if (isNaN(amt)) { toast.error(t('pos.messages.enterReceivedAmount')); return; }

    if (payMethodId === 0) { toast.error(t('pos.messages.selectPaymentMethod')); return; }

    const cashLocal = methods.find(m => m.nameAr === "نقدي")?.id;
    if (payMethodId === cashLocal && paySafeId === 0) { toast.error(t('pos.messages.selectSafe')); return; }
    if (payMethodId !== cashLocal && payMethodId !== creditMethodId && payBankId === 0) { toast.error(t('pos.messages.selectBank')); return; }

    // Prepare override data
    const paymentData = {
      paymentMethodId: payMethodId,
      safeId: payMethodId === cashLocal ? paySafeId : 0,
      bankId: (payMethodId !== cashLocal && payMethodId !== creditMethodId) ? payBankId : 0,
      paidAmount: amt,
    };

    setForm(f => ({
      ...f,
      ...paymentData
    }));

    // Pass data directly to ensure it is used
    handleSubmit(null, paymentData);
  }


  function cancelPayment() {
    setPayModalOpen(false);
  }

  function changeLineUnit(unitId) {
    const ti = selectedLineIndex != null ? selectedLineIndex : (form.lines.length > 0 ? form.lines.length - 1 : null);
    if (ti == null) { toast.info(t('pos.messages.selectCartLine')); return; }
    setForm(f => {
      const lines = [...f.lines];
      const ln = lines[ti];
      const units = Array.isArray(ln.unitsList) ? ln.unitsList : [];
      const u = units.find(x => Number(x.unitId) === Number(unitId));
      const newPrice = u?.saleprice != null ? parseFloat(u.saleprice).toFixed(decimalPlaces) : ln.price;
      lines[ti] = { ...ln, unitId: Number(unitId), unitName: u?.unitNameAr || ln.unitName, price: newPrice };
      return { ...f, lines };
    });
    setSelectedLineIndex(ti);
  }

  function parkCurrentOrder() {
    const list = JSON.parse(localStorage.getItem("parkedOrders") || "[]");
    localStorage.setItem("parkedOrders", JSON.stringify([...list, form]));
    toast.success(t('pos.messages.orderParked'));
    setForm(f => ({ ...f, lines: [] }));
    setSelectedLineIndex(null);
  }

  function recallParkedOrder() {
    const list = JSON.parse(localStorage.getItem("parkedOrders") || "[]");
    if (!Array.isArray(list) || list.length === 0) { toast.info(t('pos.messages.noParkedOrders')); return; }
    const order = list[list.length - 1];
    const rest = list.slice(0, list.length - 1);
    localStorage.setItem("parkedOrders", JSON.stringify(rest));
    setForm(order);
    toast.success(t('pos.messages.orderRecalled'));
  }

  // Same behaviour as the old inline handler on the numbered slot buttons
  function toggleParkedSlot(i) {
    if (parkedSlots[i]) {
      setForm(parkedSlots[i]);
      setParkedSlots((slots) => {
        const copy = [...slots];
        copy[i] = null;
        return copy;
      });
    } else {
      if (!form.lines || form.lines.length === 0) {
        toast.info(t('pos.messages.emptyCart'));
        return;
      }
      setParkedSlots((slots) => {
        const copy = [...slots];
        copy[i] = { ...form };
        return copy;
      });
      setForm(f => ({ ...f, lines: [] }));
      toast.success(`تم تعليق الفاتورة على الزر ${i + 1}`);
    }
  }

  function calculateTotals() {
    let subtotal = 0, totalLineDiscounts = 0, totalVAT = 0;
    form.lines.forEach(ln => {
      const qty = parseFloat(ln.qty) || 0;
      const price = parseFloat(ln.price) || 0;
      const d = parseFloat(ln.discount) || 0;
      const r = parseFloat(ln.taxRate) || 0;
      const gross = qty * price;
      const afterDisc = gross - d;
      subtotal += gross;
      totalLineDiscounts += d;
      if (priceIncludesVAT) {
        const taxable = afterDisc / (1 + r);
        totalVAT += afterDisc - taxable;
      } else {
        totalVAT += afterDisc * r;
      }
    });
    const docDiscount = parseFloat(form.discount) || 0;
    const taxableAfterDocDisc = (priceIncludesVAT ? (subtotal - totalLineDiscounts) : (subtotal - totalLineDiscounts)) - docDiscount;
    const net = priceIncludesVAT ? (subtotal - totalLineDiscounts - docDiscount) : (taxableAfterDocDisc + totalVAT);
    return { subtotal, totalLineDiscounts, docDiscount, totalVAT, net, taxableAfterDocDisc };
  }

  const totals = calculateTotals();

  useEffect(() => {
    if (form.paymentMethodId !== creditMethodId) {
      setForm(f => ({ ...f, paidAmount: parseFloat(totals.net.toFixed(decimalPlaces)) }));
    }
  }, [form.paymentMethodId, totals.net, decimalPlaces, creditMethodId]);

  function handleChange(e) {
    const { name, value } = e.target;
    let newValue = value;
    if (["customerId", "stockId", "paymentMethodId", "safeId", "bankId"].includes(name)) newValue = +value;
    if (name === "discount") newValue = parseFloat(value).toFixed(decimalPlaces);
    if (name === "paidAmount") newValue = +value;
    setForm(f => ({ ...f, [name]: newValue }));
  }

  async function handleSubmit(e, overrides = {}) {
    if (e && e.preventDefault) e.preventDefault();

    // ✅ Merge current form with overrides (to solve async state issue)
    const data = { ...form, ...overrides };

    const fallbackCustomerId = Number(settings.defaultCustomerId || 0) || Number(customers[0]?.id || 0);
    const localCustomerId = Number(data.customerId || 0) > 0 ? Number(data.customerId) : fallbackCustomerId;
    if (!localCustomerId || localCustomerId === 0) { toast.error(t('pos.messages.customerRequired')); return; }
    if (data.paymentMethodId === 0) { toast.error(t('pos.messages.selectPaymentMethod')); return; }
    const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
    if (data.paymentMethodId === cashId && !data.safeId) { toast.error(t('pos.messages.selectSafe')); return; }
    if (data.paymentMethodId !== cashId && !data.bankId && data.paymentMethodId !== creditMethodId) { toast.error(t('pos.messages.selectBank')); return; }
    if (data.lines.length === 0) { toast.error(t('pos.messages.addItemsFirst')); return; }

    // Fix floating point comparison issues
    const netRounded = parseFloat(totals.net.toFixed(decimalPlaces));
    const paidRounded = parseFloat(parseFloat(data.paidAmount || 0).toFixed(decimalPlaces));

    if (Number(data.invoiceTypeId) !== 3 && paidRounded < netRounded) { toast.error(t('pos.messages.amountLessThanNet')); return; }

    const payloadItems = data.lines.map(ln => {
      let unitPrice = parseFloat(ln.price);
      if (priceIncludesVAT) unitPrice = unitPrice / (1 + parseFloat(ln.taxRate));
      return { productId: ln.productId, unitId: ln.unitId, quantity: parseFloat(ln.qty), unitPrice, discount: parseFloat(ln.discount), taxPercent: parseFloat(ln.taxRate), description: ln.description || "" };
    });

    const invoiceStatusToSend = settings.autoApproveInvoices ? 1 : 0;
    const payload = {
      companyId, branchId,
      invoiceTypeId: data.invoiceTypeId,
      invoiceNumber: data.invoiceNumber,
      issueDate: new Date().toISOString(),
      referenceNumber: "-1",
      referenceDate: new Date().toISOString(),
      customerId: localCustomerId,
      supplierId: 0,
      stockId: data.stockId,
      paymentMethodId: data.paymentMethodId,
      safeId: data.safeId,
      bankId: data.bankId,
      posSessionId: session?.id, // ✅ Send Session ID
      notes: "",
      discount: parseFloat(data.discount),
      invoiceStatus: invoiceStatusToSend,
      paidAmount: parseFloat(data.paidAmount || 0),
      items: payloadItems,
    };
    try {
      const res = await invoiceApi.create(payload);
      const invId = res.data?.invGlobalID;
      toast.success(invoiceStatusToSend === 0 ? t('pos.messages.invoiceSavedDraft') : t('pos.messages.invoiceSaved'));
      setReturnModalOpen(false);
      if (invId) {
        window.open(`/invoices/printPos/${invId}`, "_blank");
      }
      setForm(f => ({ ...f, lines: [], discount: parseFloat(0).toFixed(decimalPlaces) }));
      invoiceApi.getNextNumber(companyId, branchId, data.invoiceTypeId, userId).then(r => {
        // ✅ التوافق: إذا كانت الاستجابة object بها displayNumber نستخدمه، وإلا نستخدم القيمة القديمة
        const respData = r.data;
        const displayNumber =
          (respData && typeof respData === "object" && respData.displayNumber)
            ? respData.displayNumber
            : (respData?.toString?.() ?? respData);
        setForm(f => ({ ...f, invoiceNumber: displayNumber }));
      });
      setPayModalOpen(false);
      exitReturnMode();
    } catch (err) {
      const serverError = err.response?.data?.errors || err.response?.data || err.message;
      toast.error(t('pos.messages.saveErrorPrefix') + JSON.stringify(serverError));
    }
  }

  const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
  const isCash = form.paymentMethodId === cashId;
  const isCredit = form.paymentMethodId === creditMethodId;
  const isNonCash = !isCash && !isCredit;
  const netRounded = parseFloat(totals.net.toFixed(decimalPlaces));
  const changeDue = isCash ? Math.max(0, parseFloat(form.paidAmount || 0) - netRounded) : 0;

  // Theme & Logic
  const theme = useMemo(() => ({
    primary: posSettings?.primaryColor || '#1d4ed8',
    bg: posSettings?.backgroundColor || '#eef1f5',
    text: posSettings?.textColor || '#0f172a',
  }), [posSettings]);

  const visibleCategories = useMemo(() => {
    return categories.filter(c => {
      const branchMatch = c.allBranches || c.branchId === branchId;
      const showInPos = c.showInPos !== false;
      return branchMatch && showInPos;
    });
  }, [categories, branchId]);

  useEffect(() => {
    if (activeCategoryId && !visibleCategories.find(c => c.id === activeCategoryId)) {
      setActiveCategoryId(null);
    }
  }, [visibleCategories, activeCategoryId]);

  const showCats = (posSettings?.showCategories ?? true);
  const showProds = (posSettings?.showProducts ?? true);

  const filteredProducts = useMemo(() => {
    // 1. Check if products should be shown based on global POS settings
    if (!showProds) {
      return [];
    }

    let list = products;

    // Filter products based on individual ShowInPOS setting
    list = list.filter(p => p.showInPOS !== false);

    // 2. Filter products based on selected category
    if (activeCategoryId) {
      list = list.filter(p => Number(p.categoryId) === Number(activeCategoryId));
    } else {
      // Only show products from visible categories
      if (categories.length > 0) {
        const visibleCategoryIds = visibleCategories.map(c => c.id);
        list = list.filter(p => visibleCategoryIds.includes(Number(p.categoryId)));
      }
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(p => (p.arabicName || "").toLowerCase().includes(term) || (p.englishName || "").toLowerCase().includes(term));
    }
    return list;
  }, [products, activeCategoryId, searchTerm, showProds, visibleCategories, categories]);

  const displayLines = useMemo(() => (form.lines || []).map((ln, i) => ({ ln, i })).reverse(), [form.lines]);
  const modalChangeDue = Math.max(0, (parseFloat(payAmount || 0) || 0) - parseFloat(totals.net.toFixed(decimalPlaces)));
  const currentCustomerName = useMemo(() => {
    const c = customers.find(c => Number(c.id) === Number(form.customerId));
    return c?.name || t('pos.customer.cash');
  }, [customers, form.customerId, t]);
  const filteredCustomers = useMemo(() => {
    const term = customerSearch.toLowerCase().trim();
    if (!term) return customers;
    return customers.filter(c => (c.name || "").toLowerCase().includes(term));
  }, [customers, customerSearch]);

  // Fit the screen exactly under whatever layout header sits above it
  const rootRef = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(null);
  useLayoutEffect(() => {
    function measure() {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top + window.scrollY;
      setViewportHeight(Math.max(480, window.innerHeight - top));
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const [productPage, setProductPage] = useState(0);
  const PRODUCT_PAGE_SIZE = 20;

  useEffect(() => {
    setProductPage(0);
  }, [activeCategoryId, searchTerm]);

  const pagedProducts = useMemo(() => {
    const start = productPage * PRODUCT_PAGE_SIZE;
    return filteredProducts.slice(start, start + PRODUCT_PAGE_SIZE);
  }, [filteredProducts, productPage]);

  const totalProductPages = Math.ceil(filteredProducts.length / PRODUCT_PAGE_SIZE);

  const [categoryPage, setCategoryPage] = useState(0);
  const CATEGORY_PAGE_SIZE = 12;

  const pagedCategories = useMemo(() => {
    const start = categoryPage * CATEGORY_PAGE_SIZE;
    return visibleCategories.slice(start, start + CATEGORY_PAGE_SIZE);
  }, [visibleCategories, categoryPage]);

  const totalCategoryPages = Math.ceil(visibleCategories.length / CATEGORY_PAGE_SIZE);

  function addProductToCart(p) {
    const unitsList = Array.isArray(p.units) ? p.units : [];
    const baseUnit = unitsList.find(u => u.isBaseUnit) || {};

    setForm(f => {
      const idx = f.lines.findIndex(l => l.productId === p.id && l.unitId === (baseUnit.unitId || 0));
      let newLines = [...f.lines];
      let line;

      if (idx >= 0) {
        // If exists, extract it to move it to the end (which will be the top after reverse)
        const existingLine = newLines[idx];
        const q = parseFloat(existingLine.qty) + 1;
        line = { ...existingLine, qty: q.toFixed(decimalPlaces) };
        // Remove from current position
        newLines.splice(idx, 1);
      } else {
        // New item
        line = {
          productId: p.id,
          productName: p.arabicName || p.nameAr || p.name || "",
          description: p.description || "",
          unitId: baseUnit.unitId || 0,
          unitName: baseUnit.unitNameAr || "",
          qty: parseFloat(1).toFixed(decimalPlaces),
          price: pickUnitPriceForSale(baseUnit, p),
          discount: parseFloat(0).toFixed(decimalPlaces),
          taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
          unitsList,
        };
      }

      // Add to the end of the array
      newLines.push(line);
      return { ...f, lines: newLines };
    });

    // The newly added/updated item will be at index (length - 1) in f.lines,
    // and thus at index 0 in displayLines.
    setSelectedLineIndex(null); // Or we can set it to the new last index
  }

  /* ---------------- layout: categories | products | cart ---------------- */
  const hasCats = showCats && visibleCategories.length > 0;
  let gridCols = "lg:grid-cols-[200px_minmax(0,1fr)_minmax(380px,32%)]";
  if (!hasCats && !showProds) gridCols = "lg:grid-cols-1";
  else if (!hasCats) gridCols = "lg:grid-cols-[minmax(0,1fr)_minmax(380px,34%)]";
  else if (!showProds) gridCols = "lg:grid-cols-[240px_minmax(0,1fr)]";

  const fmt = (n) => Number(n || 0).toFixed(decimalPlaces);

  // Displayed card price = same price that gets added to the cart (base unit first)
  const productDisplayPrice = (p) => {
    const units = Array.isArray(p.units) ? p.units : [];
    const baseUnit = units.find(u => u.isBaseUnit) || units[0] || {};
    return pickUnitPriceForSale(baseUnit, p);
  };
  const cartCount = form.lines.length;
  const isReturnMode = Number(form.invoiceTypeId) === 3;

  // Button styles used across the screen
  const pagerBtn = "h-9 w-9 flex items-center justify-center rounded-lg bg-white/90 text-slate-800 hover:bg-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition";
  const actionBtn = "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-semibold text-white shadow-sm transition active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap";

  // Each category gets its own colour; products inherit it
  const CATEGORY_COLORS = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#db2777", "#65a30d", "#ea580c", "#4f46e5", "#0d9488", "#9333ea"];
  const categoryColor = (categoryId) => {
    const idx = visibleCategories.findIndex(c => Number(c.id) === Number(categoryId));
    return idx >= 0 ? CATEGORY_COLORS[idx % CATEGORY_COLORS.length] : theme.primary;
  };
  const tint = (hex, alpha) => `${hex}${alpha}`; // hex + 2-digit alpha, e.g. tint("#2563eb","1a")

  return (
    <div ref={rootRef} dir="rtl" className="overflow-hidden flex flex-col" style={{ height: viewportHeight ? `${viewportHeight}px` : "100vh", backgroundColor: theme.bg, color: theme.text }}>

      {/* ============================ Header ============================ */}
      <header className="shrink-0 text-white shadow-md" style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.primary}dd)` }}>
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              {Icon.cart()}
            </div>
            <div className="leading-tight">
              <div className="font-bold text-base">{t('pos.title')}</div>
              <div className="text-xs text-white/80 tabular-nums">
                {t('pos.invoiceLabel')} {form.invoiceNumber || "—"}
              </div>
            </div>
            {isReturnMode && (
              <span className="ms-1 text-xs font-bold px-2 py-1 rounded-md bg-amber-400 text-amber-950">
                {t('pos.buttons.return')}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-1 min-w-[280px] max-w-2xl mx-auto">
            <div className="relative flex-1">
              <span className="absolute inset-y-0 start-3 flex items-center text-slate-400 pointer-events-none">{Icon.search()}</span>
              <Input
                placeholder={t('pos.placeholders.search')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="h-10 ps-9 bg-white border-0 text-slate-900 shadow-sm"
              />
            </div>
            <div className="relative w-44">
              <span className="absolute inset-y-0 start-3 flex items-center text-slate-400 pointer-events-none">{Icon.barcode()}</span>
              <Input
                placeholder={t('pos.placeholders.barcode')}
                value={barcode}
                onChange={e => setBarcode(e.target.value)}
                onKeyDown={handleBarcodeKeyDown}
                disabled={barcodeLoading}
                className="h-10 ps-9 bg-white border-0 text-slate-900 shadow-sm tabular-nums"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" className={actionBtn + " bg-indigo-500 hover:bg-indigo-600"} onClick={() => navigate('/reports/all-invoices?invoiceTypeId=11')}>
              {Icon.receipt()} <span className="hidden xl:inline">{t('pos.buttons.posInvoices')}</span>
            </button>
            <button type="button" className={actionBtn + " bg-amber-500 hover:bg-amber-600"} onClick={() => navigate('/reports/all-invoices?invoiceTypeId=11&invoiceStatus=1')}>
              {Icon.undo()} <span className="hidden xl:inline">{t('pos.buttons.return')}</span>
            </button>
            <button type="button" className={actionBtn + " bg-red-600 hover:bg-red-700"} onClick={handleCloseSessionClick} title={t('pos.session.closeTitle')}>
              {Icon.power()} <span className="hidden xl:inline">{t('pos.session.close')}</span>
            </button>
          </div>
        </div>
      </header>

      {/* ============================ Main ============================ */}
      <main className={`flex-1 min-h-0 grid grid-cols-1 ${gridCols} gap-3 p-3`}>

        {/* ---------- Categories ---------- */}
        {hasCats && (
          <aside className="min-h-0 bg-white rounded-xl shadow-md flex flex-col overflow-hidden">
            <div className="px-3 py-2.5 text-white flex items-center justify-between" style={{ backgroundColor: theme.primary }}>
              <span className="text-sm font-bold">{t('pos.headers.categories')}</span>
              {totalCategoryPages > 1 && (
                <span className="text-xs text-white/80 tabular-nums" dir="ltr">{categoryPage + 1} / {totalCategoryPages}</span>
              )}
            </div>

            <nav className="flex-1 min-h-0 overflow-y-auto p-2 flex lg:flex-col gap-1.5 bg-slate-50">
              {[{ id: null, nameAr: t('common.all') }, ...pagedCategories].map(c => {
                const active = c.id === null ? activeCategoryId == null : activeCategoryId === c.id;
                const color = c.id === null ? "#334155" : categoryColor(c.id);
                return (
                  <button
                    key={c.id ?? "all"}
                    type="button"
                    onClick={() => setActiveCategoryId(c.id)}
                    className="shrink-0 text-right px-3 py-2.5 rounded-lg text-sm font-bold transition border-s-4 shadow-sm hover:brightness-95"
                    style={active
                      ? { backgroundColor: color, borderInlineStartColor: color, color: "#fff" }
                      : { backgroundColor: tint(color, "14"), borderInlineStartColor: color, color }}
                  >
                    {c.nameAr}
                  </button>
                );
              })}
            </nav>

            {totalCategoryPages > 1 && (
              <div className="p-2 grid grid-cols-2 gap-2" style={{ backgroundColor: theme.primary }}>
                <button type="button" className={pagerBtn + " w-full h-10"} onClick={() => setCategoryPage(p => Math.max(0, p - 1))} disabled={categoryPage === 0}>
                  {Icon.chevron()}
                </button>
                <button type="button" className={pagerBtn + " w-full h-10"} onClick={() => setCategoryPage(p => Math.min(totalCategoryPages - 1, p + 1))} disabled={categoryPage >= totalCategoryPages - 1}>
                  <span className="inline-flex rotate-180">{Icon.chevron()}</span>
                </button>
              </div>
            )}
          </aside>
        )}

        {/* ---------- Products ---------- */}
        {showProds && (
          <section className="min-h-0 bg-white rounded-xl shadow-md flex flex-col overflow-hidden">
            <div className="px-4 py-2 text-white flex items-center justify-between gap-3" style={{ backgroundColor: theme.primary }}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold">{t('pos.headers.availableProducts')}</span>
                <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 tabular-nums">{filteredProducts.length} {t('common.items', 'صنف')}</span>
              </div>

              {totalProductPages > 1 && (
                <div className="flex items-center gap-2">
                  <button type="button" className={pagerBtn} onClick={() => setProductPage(p => Math.max(0, p - 1))} disabled={productPage === 0} aria-label="Previous page">
                    {Icon.chevron()}
                  </button>
                  <span dir="ltr" className="text-sm font-bold tabular-nums min-w-[48px] text-center">
                    {productPage + 1} / {totalProductPages}
                  </span>
                  <button type="button" className={pagerBtn} onClick={() => setProductPage(p => Math.min(totalProductPages - 1, p + 1))} disabled={productPage >= totalProductPages - 1} aria-label="Next page">
                    <span className="inline-flex rotate-180">{Icon.chevron()}</span>
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-slate-100/70">
              {pagedProducts.length === 0 ? (
                <div className="h-full min-h-[240px] flex flex-col items-center justify-center text-slate-400 gap-2">
                  {Icon.box()}
                  <p className="font-medium">{t('pos.messages.noProductsFound')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5">
                  {pagedProducts.map(p => {
                    const color = categoryColor(p.categoryId);
                    const qtyInCart = form.lines
                      .filter(l => l.productId === p.id)
                      .reduce((s, l) => s + (parseFloat(l.qty) || 0), 0);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addProductToCart(p)}
                        className="relative text-right rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition active:scale-[0.97] flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 border-t-4"
                        style={{ borderTopColor: color }}
                      >
                        {qtyInCart > 0 && (
                          <span className="absolute top-1.5 left-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full text-white text-xs font-bold flex items-center justify-center shadow tabular-nums" style={{ backgroundColor: color }}>
                            {qtyInCart}
                          </span>
                        )}

                        {posSettings && posSettings.showImages && p.imageUrl && (
                          <div className="w-full aspect-[4/3] overflow-hidden bg-slate-100">
                            <img src={p.imageUrl} alt={p.arabicName || p.nameAr} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                        )}

                        <div className="flex-1 p-3 pb-2" style={{ backgroundColor: tint(color, "0d") }}>
                          <div className="font-bold text-slate-900 text-sm leading-snug line-clamp-2 min-h-[2.5em]">
                            {p.arabicName}
                          </div>
                          {posSettings && posSettings.showBarcode && p.barcode && (
                            <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums" dir="ltr">{p.barcode}</div>
                          )}
                        </div>

                        <div className="px-3 py-1.5 flex items-center justify-between" style={{ backgroundColor: tint(color, "1f") }}>
                          <span className="text-base font-extrabold tabular-nums" style={{ color }}>
                            {productDisplayPrice(p)}
                          </span>
                          <span className="h-6 w-6 rounded-md text-white flex items-center justify-center text-lg leading-none" style={{ backgroundColor: color }}>+</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {/* ---------- Cart ---------- */}
        <section className="min-h-0 bg-white rounded-xl shadow-md flex flex-col overflow-hidden">

          <div className="px-3 py-2 text-white flex items-center justify-between" style={{ backgroundColor: theme.primary }}>
            <span className="font-bold text-sm flex items-center gap-2">{Icon.cart("w-4 h-4")} {t('pos.headers.cart')}</span>
            <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 tabular-nums">{cartCount}</span>
          </div>

          {/* Customer + VAT + parked slots */}
          <div className="p-3 space-y-2.5 bg-indigo-50/70 border-b border-indigo-100">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openCustomerModal}
                className="flex-1 min-w-0 inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-white border border-indigo-200 hover:bg-indigo-50 transition text-right shadow-sm"
              >
                <span className="h-6 w-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">{Icon.user("w-3.5 h-3.5")}</span>
                <span className="text-xs text-indigo-500 shrink-0">{t('invoice.fields.customer')}</span>
                <span className="text-sm font-bold text-indigo-900 truncate">{currentCustomerName}</span>
              </button>
              <button
                type="button"
                onClick={togglePriceIncludesVAT}
                title={t('programSettings.priceIncludesVAT')}
                className={`h-10 px-3 rounded-lg text-xs font-bold border transition whitespace-nowrap shadow-sm ${
                  priceIncludesVAT
                    ? "bg-emerald-500 text-white border-emerald-600"
                    : "bg-amber-400 text-amber-950 border-amber-500"
                }`}
              >
                {priceIncludesVAT ? t('programSettings.priceIncludesVAT') : t('pos.policy.priceExcludesVAT')}
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              {Array.from({ length: 5 }, (_, i) => {
                const held = parkedSlots[i];
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleParkedSlot(i)}
                    className={`relative flex-1 h-9 rounded-lg text-sm font-bold tabular-nums transition shadow-sm ${
                      held
                        ? "bg-emerald-500 text-white hover:bg-emerald-600"
                        : "bg-indigo-500 text-white hover:bg-indigo-600"
                    }`}
                  >
                    {i + 1}
                    {held && (
                      <span className="absolute -top-1.5 -left-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-emerald-700 text-[10px] font-bold border border-emerald-300 flex items-center justify-center">
                        {held.lines?.length || 0}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Line actions */}
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5 overflow-x-auto">
            <button type="button" className={actionBtn + " bg-purple-600 hover:bg-purple-700"} onClick={setLineDiscountPercent} disabled={cartCount === 0}>
              {Icon.percent()} {t('pos.buttons.discountPercent')}
            </button>
            <button type="button" className={actionBtn + " bg-orange-500 hover:bg-orange-600"} onClick={changeLinePrice} disabled={cartCount === 0}>
              {Icon.pencil()} {t('pos.buttons.changePrice')}
            </button>
            <button type="button" className={actionBtn + " bg-red-600 hover:bg-red-700"} onClick={removeSelectedLine} disabled={cartCount === 0}>
              {Icon.trash()} {t('invoice.fields.delete')}
            </button>
            <button type="button" className={actionBtn + " ms-auto bg-slate-500 hover:bg-slate-600"} onClick={clearCart} disabled={cartCount === 0}>
              {Icon.broom()} {t('pos.buttons.clear')}
            </button>
          </div>

          {/* Lines */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {cartCount === 0 ? (
              <div className="h-full min-h-[160px] flex flex-col items-center justify-center gap-2 px-6 text-center" style={{ color: tint(theme.primary, "80") }}>
                {Icon.cart("w-12 h-12")}
                <p className="text-sm font-semibold">{t('pos.messages.emptyCart')}</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {displayLines.map(({ ln, i: origIndex }, j) => {
                  const qty = parseFloat(ln.qty);
                  const price = parseFloat(ln.price);
                  const discount = parseFloat(ln.discount);
                  const r = parseFloat(ln.taxRate);
                  const afterDisc = qty * price - discount;
                  const taxable = priceIncludesVAT ? afterDisc / (1 + r) : afterDisc;
                  const vat = priceIncludesVAT ? afterDisc - taxable : afterDisc * r;
                  const net = priceIncludesVAT ? afterDisc : afterDisc + vat;
                  const selected = selectedLineIndex === origIndex;
                  const prod = products.find(p => p.id === ln.productId);
                  const color = categoryColor(prod?.categoryId);

                  return (
                    <li
                      key={origIndex}
                      onClick={() => setSelectedLineIndex(origIndex)}
                      className="px-3 py-2.5 cursor-pointer transition border-s-4"
                      style={{
                        borderInlineStartColor: selected ? theme.primary : color,
                        backgroundColor: selected ? tint(theme.primary, "14") : undefined,
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <span className="h-6 w-6 shrink-0 rounded-full text-white text-xs font-bold flex items-center justify-center tabular-nums" style={{ backgroundColor: color }}>
                          {j + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm text-slate-900 leading-snug line-clamp-2">{ln.productName}</div>
                          <div className="text-xs text-slate-500 mt-0.5 tabular-nums">
                            <span className="font-semibold text-amber-700">{ln.price}</span>
                            {ln.unitName ? ` · ${ln.unitName}` : ""}
                            {discount > 0 && <span className="text-rose-600 font-semibold"> − {fmt(discount)}</span>}
                          </div>
                        </div>
                        <div className="text-sm font-extrabold text-emerald-700 tabular-nums whitespace-nowrap">{net.toFixed(decimalPlaces)}</div>
                      </div>

                      <div className="flex items-center justify-between mt-2 ps-8">
                        <div className="inline-flex items-center rounded-lg overflow-hidden shadow-sm" dir="ltr">
                          <button
                            type="button"
                            className="h-8 w-9 text-lg font-bold text-white bg-rose-500 hover:bg-rose-600"
                            onClick={(e) => { e.stopPropagation(); handleQty(origIndex, -1); }}
                            aria-label="−"
                          >−</button>
                          <div className="h-8 min-w-[52px] px-2 flex items-center justify-center text-sm font-bold tabular-nums bg-sky-50 text-sky-800">
                            {ln.qty}
                          </div>
                          <button
                            type="button"
                            className="h-8 w-9 text-lg font-bold text-white bg-emerald-500 hover:bg-emerald-600"
                            onClick={(e) => { e.stopPropagation(); handleQty(origIndex, +1); }}
                            aria-label="+"
                          >+</button>
                        </div>
                        <button
                          type="button"
                          className="h-8 w-8 flex items-center justify-center rounded-lg text-red-600 bg-red-50 hover:text-white hover:bg-red-600 transition"
                          onClick={(e) => { e.stopPropagation(); handleRemoveLine(origIndex); }}
                          aria-label={t('invoice.fields.delete')}
                        >
                          {Icon.trash()}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Totals + pay */}
          <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-2.5">
            <dl className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-sky-100 py-1.5">
                <dt className="text-[11px] text-sky-700 font-semibold">{t('pos.summary.total')}</dt>
                <dd className="text-sm font-extrabold tabular-nums text-sky-900">{fmt(totals.subtotal)}</dd>
              </div>
              <div className="rounded-lg bg-orange-100 py-1.5">
                <dt className="text-[11px] text-orange-700 font-semibold">{t('pos.summary.tax')}</dt>
                <dd className="text-sm font-extrabold tabular-nums text-orange-900">{fmt(totals.totalVAT)}</dd>
              </div>
              <div className="rounded-lg bg-purple-100 py-1.5">
                <dt className="text-[11px] text-purple-700 font-semibold">{t('pos.summary.discount')}</dt>
                <dd className="text-sm font-extrabold tabular-nums text-purple-900">{fmt((totals.docDiscount || 0) + (totals.totalLineDiscounts || 0))}</dd>
              </div>
            </dl>

            <button
              type="button"
              onClick={openPaymentModal}
              disabled={cartCount === 0}
              className="w-full rounded-xl text-white px-4 py-3 flex items-center justify-between shadow-lg transition active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300"
              style={{ background: isReturnMode ? "linear-gradient(90deg,#d97706,#b45309)" : "linear-gradient(90deg,#059669,#047857)" }}
            >
              <span className="text-start">
                <span className="block text-xs opacity-90">{t('pos.summary.net')}</span>
                <span className="block text-3xl font-extrabold tabular-nums leading-tight">{fmt(totals.net)}</span>
              </span>
              <span className="text-lg font-bold bg-white/20 rounded-lg px-4 py-2">
                {t('pos.buttons.payNow')}
              </span>
            </button>
          </div>
        </section>
      </main>

      {/* ============================ Numpad modal ============================ */}
      {padOpen && (
        <Modal maxWidth="max-w-xs">
          <div className="p-5">
            <div className="text-center font-bold text-lg text-slate-900 mb-3">{padTitle}</div>
            <Input
              value={padValue}
              onChange={e => setPadValue(e.target.value)}
              onFocus={e => e.target.select()}
              onKeyDown={e => { if (e.key === "Enter") numPadConfirm(); if (e.key === "Escape") numPadCancel(); }}
              autoFocus
              dir="ltr"
              className="w-full h-14 text-center text-3xl font-bold tabular-nums mb-3 bg-slate-50 border-slate-200"
            />
            <Keypad onKey={handlePadKeyPress} onBackspace={numPadBackspace} />
            <button type="button" onClick={numPadClear} className="w-full mt-2 h-10 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition">
              {t('pos.pad.clear')}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 p-3 border-t border-slate-100 bg-slate-50">
            <button type="button" className="h-12 rounded-xl font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100" onClick={numPadCancel}>
              {t('pos.pad.cancel')}
            </button>
            <button type="button" className="h-12 rounded-xl font-bold text-white hover:opacity-90" style={{ backgroundColor: theme.primary }} onClick={numPadConfirm}>
              {t('pos.pad.confirm')}
            </button>
          </div>
        </Modal>
      )}

      {/* ============================ Customer modal ============================ */}
      {customerModalOpen && (
        <Modal maxWidth="max-w-md">
          <div className="p-5 pb-3 border-b border-slate-100">
            <div className="font-bold text-lg text-slate-900 mb-3">{t('pos.customerModal.title')}</div>
            <div className="relative">
              <span className="absolute inset-y-0 start-3 flex items-center text-slate-400 pointer-events-none">{Icon.search()}</span>
              <Input
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                placeholder={t('pos.placeholders.customerSearch')}
                autoFocus
                className="h-11 ps-9 bg-slate-50 border-slate-200"
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 max-h-80 overflow-y-auto p-2">
            {filteredCustomers.map(c => {
              const active = Number(c.id) === Number(form.customerId);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => chooseCustomer(c.id)}
                  className={`w-full text-right px-3 h-12 rounded-lg text-sm font-medium flex items-center justify-between transition ${active ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  <span className="truncate">{c.name}</span>
                  {active && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: theme.primary }} />}
                </button>
              );
            })}
            {filteredCustomers.length === 0 && (
              <div className="text-center text-slate-500 py-8 text-sm">{t('pos.customerModal.noResults')}</div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 p-3 border-t border-slate-100 bg-slate-50">
            <button type="button" className="h-11 rounded-xl font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100" onClick={cancelCustomer}>
              {t('pos.customerModal.cancel')}
            </button>
            <button type="button" className="h-11 rounded-xl font-bold text-white hover:opacity-90" style={{ backgroundColor: theme.primary }} onClick={() => setCustomerModalOpen(false)}>
              {t('pos.customerModal.confirm')}
            </button>
          </div>
        </Modal>
      )}

      {/* ============================ Payment modal ============================ */}
      {payModalOpen && (
        <Modal maxWidth="max-w-4xl">
          <div className="px-6 py-4 flex items-center justify-between shrink-0 text-white" style={{ backgroundColor: theme.primary }}>
            <h3 className="text-lg font-bold">{t('pos.payment.title')}</h3>
            <span className="text-sm text-white/80 tabular-nums">{t('pos.invoiceLabel')} {form.invoiceNumber}</span>
          </div>

          <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
            {/* Summary + method */}
            <div className="flex-1 p-6 flex flex-col gap-5 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl text-white p-4" style={{ backgroundColor: theme.primary }}>
                  <div className="text-xs opacity-70 mb-1">{t('pos.summary.net')}</div>
                  <div className="text-3xl font-extrabold tabular-nums">{fmt(totals.net)}</div>
                </div>
                {payMethodId === cashId ? (
                  <div className={`rounded-xl p-4 border ${modalChangeDue > 0 ? "bg-emerald-500 border-emerald-600 text-white" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                    <div className="text-xs mb-1">{t('pos.payment.changeDue')}</div>
                    <div className="text-3xl font-extrabold tabular-nums">{fmt(modalChangeDue)}</div>
                  </div>
                ) : <div />}
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-700 mb-2">{t('pos.payment.method')}</div>
                <div className="grid grid-cols-2 gap-2">
                  {methods.map(m => {
                    const isSelected = payMethodId === m.id;
                    const name = m.nameAr || "";
                    const mColor = name.includes("نقدي") ? "#059669"
                      : (name.includes("شبكة") || name.includes("بطاقة")) ? "#2563eb"
                      : name.includes("آجل") ? "#d97706" : "#7c3aed";
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setPayMethodId(m.id)}
                        aria-pressed={isSelected}
                        className={`h-14 rounded-xl text-base font-bold border-2 transition ${isSelected ? "text-white shadow-md scale-[1.02]" : ""}`}
                        style={isSelected
                          ? { backgroundColor: mColor, borderColor: mColor }
                          : { backgroundColor: `${mColor}14`, borderColor: `${mColor}55`, color: mColor }}
                      >
                        {m.nameAr}
                      </button>
                    );
                  })}
                </div>
              </div>

              {payMethodId === cashId && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">{t('invoice.fields.safe')}</label>
                  <select
                    value={paySafeId}
                    onChange={e => setPaySafeId(Number(e.target.value))}
                    className="w-full h-12 px-3 border border-slate-300 rounded-xl bg-white font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:bg-slate-50"
                    disabled={!!restrictedSafeId}
                  >
                    <option value={0}>{t('pos.messages.selectSafe')}</option>
                    {safes
                      .filter(s => {
                        if (restrictedSafeId) return s.safeId === restrictedSafeId;
                        if (allowedSafeIds.length > 0) return allowedSafeIds.includes(s.safeId);
                        return true;
                      })
                      .map(s => (
                        <option key={s.safeId} value={s.safeId}>{s.name}</option>
                      ))}
                  </select>
                </div>
              )}

              {payMethodId !== cashId && payMethodId !== creditMethodId && payMethodId !== 0 && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">{t('invoice.fields.bank')}</label>
                  <select
                    value={payBankId}
                    onChange={e => setPayBankId(Number(e.target.value))}
                    className="w-full h-12 px-3 border border-slate-300 rounded-xl bg-white font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <option value={0}>{t('pos.messages.selectBank')}</option>
                    {banks.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mt-auto grid grid-cols-[1fr_2fr] gap-3 pt-2">
                <button type="button" onClick={cancelPayment} className="h-14 rounded-xl font-semibold text-lg text-slate-700 bg-white border border-slate-300 hover:bg-slate-50">
                  {t('pos.buttons.cancel')}
                </button>
                <button type="button" onClick={confirmPayment} className="h-14 rounded-xl font-bold text-lg text-white bg-emerald-700 hover:bg-emerald-800 shadow-sm active:scale-[0.99] transition">
                  {t('pos.buttons.confirm')}
                </button>
              </div>
            </div>

            {/* Amount keypad */}
            <div className="md:w-[340px] p-6 bg-slate-50 border-t md:border-t-0 md:border-s border-slate-200 flex flex-col gap-3 shrink-0">
              <label className="text-sm font-semibold text-slate-700">{t('pos.pad.titlePaidAmount')}</label>
              <Input
                value={payAmount}
                onChange={e => { setPayAmount(e.target.value); setPayIsFirstKey(false); }}
                onFocus={e => e.target.select()}
                onKeyDown={e => { if (e.key === "Enter") confirmPayment(); }}
                autoFocus
                dir="ltr"
                className="w-full h-16 text-center text-4xl font-extrabold tabular-nums bg-white border-slate-300 text-slate-900 rounded-xl"
              />
              <button
                type="button"
                onClick={() => { setPayAmount(totals.net.toFixed(decimalPlaces)); setPayIsFirstKey(true); }}
                className="h-10 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 tabular-nums"
              >
                {t('pos.summary.net')}: {fmt(totals.net)}
              </button>
              <Keypad
                onKey={handlePayKeyPress}
                onBackspace={() => { setPayAmount(v => v.slice(0, -1)); setPayIsFirstKey(false); }}
                size="h-14 text-2xl"
              />
              <button
                type="button"
                onClick={() => { setPayAmount(""); setPayIsFirstKey(true); }}
                className="h-11 rounded-xl text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100"
              >
                {t('pos.pad.clear')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ============================ Return modal ============================ */}
      {returnModalOpen && (
        <Modal maxWidth="max-w-3xl">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
            <span className="h-9 w-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">{Icon.undo("w-5 h-5")}</span>
            <div>
              <h3 className="text-lg font-bold text-slate-900">مرتجع من فاتورة أصلية</h3>
              <p className="text-xs text-slate-500 tabular-nums">{form.referenceNumber !== "-1" ? form.referenceNumber : ""}</p>
            </div>
          </div>
          <div className="flex-1 min-h-0 max-h-[55vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-right font-semibold">الصنف</th>
                  <th className="px-3 py-2 text-center font-semibold">الوحدة</th>
                  <th className="px-3 py-2 text-center font-semibold">الأصل</th>
                  <th className="px-3 py-2 text-center font-semibold">المُرجع</th>
                  <th className="px-3 py-2 text-center font-semibold">المتاح</th>
                  <th className="px-3 py-2 text-center font-semibold">كمية الإرجاع</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {returnItems.map((it, idx) => (
                  <tr key={`${it.productId}-${it.unitId}`} className={it.availableQty === 0 ? "opacity-50" : ""}>
                    <td className="px-3 py-2.5 font-medium text-slate-900">{it.productName}</td>
                    <td className="px-3 py-2.5 text-center text-slate-600">{it.unitName}</td>
                    <td className="px-3 py-2.5 text-center tabular-nums">{it.originalQty}</td>
                    <td className="px-3 py-2.5 text-center tabular-nums text-amber-700">{it.returnedQty}</td>
                    <td className="px-3 py-2.5 text-center tabular-nums font-bold text-emerald-700">{it.availableQty}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-center" dir="ltr">
                        <div className="inline-flex items-center rounded-lg border border-slate-200 overflow-hidden">
                          <button type="button" className="h-9 w-9 text-lg text-slate-600 hover:bg-slate-100" onClick={() => setReturnItemQty(idx, Math.max(0, (it.selectQty || 0) - 1))}>−</button>
                          <input
                            value={it.selectQty}
                            onChange={e => setReturnItemQty(idx, e.target.value)}
                            className="h-9 w-16 text-center font-bold tabular-nums border-x border-slate-200 bg-slate-50 focus:outline-none focus:bg-white"
                          />
                          <button type="button" className="h-9 w-9 text-lg text-slate-600 hover:bg-slate-100" onClick={() => setReturnItemQty(idx, Math.min((it.selectQty || 0) + 1, it.availableQty))}>+</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {returnItems.length === 0 && (
                  <tr><td className="p-8 text-center text-slate-500" colSpan={6}>لا توجد أصناف قابلة للإرجاع</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-3 gap-2 p-3 border-t border-slate-100 bg-slate-50">
            <button type="button" className="h-12 rounded-xl font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100" onClick={exitReturnMode}>إلغاء المرتجع</button>
            <button type="button" className="h-12 rounded-xl font-semibold text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100" onClick={returnAll}>إرجاع الكل</button>
            <button type="button" className="h-12 rounded-xl font-bold text-white bg-emerald-700 hover:bg-emerald-800" onClick={applyReturnSelection}>إضافة للسلة</button>
          </div>
        </Modal>
      )}

      {/* ============================ Start session ============================ */}
      {startSessionModalOpen && (
        <Modal maxWidth="max-w-sm" dim="bg-slate-900/70">
          <div className="p-7 text-center">
            <div className="mx-auto h-12 w-12 rounded-xl flex items-center justify-center text-white mb-4" style={{ backgroundColor: theme.primary }}>
              {Icon.cart("w-6 h-6")}
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('pos.session.startTitle')}</h2>
            <label className="block text-sm font-semibold text-slate-600 mb-2">{t('pos.session.openingBalance')}</label>
            <Input
              type="number"
              value={openingBalance}
              onChange={e => setOpeningBalance(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleStartSession(); }}
              dir="ltr"
              className="text-center text-3xl font-bold tabular-nums h-16 bg-slate-50 border-slate-300"
              autoFocus
            />
            <button
              type="button"
              onClick={handleStartSession}
              className="w-full mt-5 h-14 text-lg font-bold text-white rounded-xl shadow-sm hover:opacity-90 transition"
              style={{ backgroundColor: theme.primary }}
            >
              {t('pos.session.start')}
            </button>
          </div>
        </Modal>
      )}

      {/* ============================ Close session ============================ */}
      {closeSessionModalOpen && sessionSummary && (() => {
        const actual = parseFloat(closingBalance);
        const hasActual = closingBalance !== "" && !isNaN(actual);
        const variance = hasActual ? actual - sessionSummary.expectedCash : 0;
        const rows = [
          { label: t('pos.session.totalCashSales'), value: sessionSummary.totalCashSales },
          { label: t('pos.session.totalCardSales'), value: sessionSummary.totalCardSales },
          { label: t('pos.session.totalBankTransferSales'), value: sessionSummary.totalBankTransferSales },
          { label: t('pos.session.totalCreditSales'), value: sessionSummary.totalCreditSales },
          { label: t('pos.session.totalReturns'), value: sessionSummary.totalReturns, negative: true },
        ];
        return (
          <Modal maxWidth="max-w-lg">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <span className="h-9 w-9 rounded-lg bg-red-50 text-red-700 flex items-center justify-center">{Icon.power("w-5 h-5")}</span>
              <h3 className="text-lg font-bold text-slate-900">{t('pos.session.closeTitle')}</h3>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <dl className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
                <div className="flex justify-between px-4 py-2.5 bg-slate-50">
                  <dt className="font-semibold text-slate-700">{t('pos.session.openingBalance')}</dt>
                  <dd className="font-bold tabular-nums">{fmt(sessionSummary.openingBalance)}</dd>
                </div>
                {rows.map(r => (
                  <div key={r.label} className="flex justify-between px-4 py-2.5">
                    <dt className="text-slate-600">{r.label}</dt>
                    <dd className={`font-semibold tabular-nums ${r.negative ? "text-rose-700" : "text-slate-900"}`}>{fmt(r.value)}</dd>
                  </div>
                ))}
                <div className="flex justify-between items-center px-4 py-3 bg-slate-900 text-white rounded-b-xl">
                  <dt className="font-semibold">{t('pos.session.expectedCash')}</dt>
                  <dd className="text-xl font-extrabold tabular-nums">{fmt(sessionSummary.expectedCash)}</dd>
                </div>
              </dl>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('pos.session.actualCash')}</label>
                <Input
                  type="number"
                  value={closingBalance}
                  onChange={e => setClosingBalance(e.target.value)}
                  dir="ltr"
                  className="text-center text-2xl font-bold tabular-nums h-14 bg-white border-slate-300"
                  autoFocus
                />
                {hasActual && (
                  <div className={`mt-2 flex justify-between items-center px-3 py-2 rounded-lg text-sm font-semibold ${
                    Math.abs(variance) < 0.005 ? "bg-slate-100 text-slate-700" : variance > 0 ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"
                  }`}>
                    <span>{t('pos.session.variance')}</span>
                    <span className="tabular-nums" dir="ltr">{variance > 0 ? "+" : ""}{fmt(variance)}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t('pos.session.notes')}</label>
                <Input value={closingNotes} onChange={e => setClosingNotes(e.target.value)} className="w-full h-11" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 p-3 border-t border-slate-100 bg-slate-50">
              <button type="button" onClick={() => setCloseSessionModalOpen(false)} className="h-12 rounded-xl font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100">
                {t('pos.buttons.cancel')}
              </button>
              <button type="button" onClick={handleConfirmCloseSession} className="h-12 rounded-xl font-bold text-white bg-red-700 hover:bg-red-800">
                {t('pos.session.close')}
              </button>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}
